import { motion } from "framer-motion";
import { useCallback, useMemo, useState } from "react";
import { useViewport } from "src/contexts";
import { Icon, ImagePlaceholder } from "~components";
import { Css } from "~generated/css";
import { useTestIds } from "~utils";

export type ImageMosaicTile = {
  id: string;
  imageUrl: string;
  /** Alt text and pill copy */
  name?: string;
  onClick?: () => void;
  /** Disables the onClick handler for optionally readonly forms */
  readonly?: boolean;
  isReversed?: boolean;
};

export type ImageMosaicDisplayProps = {
  tiles: ImageMosaicTile[];
};

/** A 1-2 column image grid layout which displays the first image as a "hero" span given an odd number of image tiles */
export function ImageMosaicDisplay(props: ImageMosaicDisplayProps) {
  const { tiles } = props;
  const { isMobile } = useViewport();
  const tileCount = tiles.length;

  // At most 2 columns: 1 col on mobile or ≤2 tiles; 2 cols on desktop with 3+ tiles.
  const columnCount = useMemo(() => (isMobile || tileCount <= 2 ? 1 : 2), [isMobile, tileCount]);

  // For most cases, we just allow each tile to be placed into the grid using the default grid behavior,
  // which chooses the next available row/column.
  // However, for odd tile counts (3 or more), instead of having a single tile that wraps to its own row,
  // we make the first tile span the full width on the first row for more balanced wrapping.
  const getTileGridColumn = useCallback(
    (index: number) => {
      if (index !== 0) return undefined;
      if (columnCount !== 2 || tileCount < 3 || tileCount % 2 === 0) return undefined;
      return "1 / -1";
    },
    [columnCount, tileCount],
  );

  if (tiles.isEmpty) return <ImagePlaceholder />;

  const gridTemplateColumns = `repeat(${columnCount}, minmax(0, 1fr))`;

  return (
    <div css={Css.df.fdc.fg1.w100.mh0.h100.mw0.relative.oh.bgOffWhite.$}>
      <div css={Css.dg.h100.w100.maxw100.mh0.mw0.fg1.gapPx(1).gtc(gridTemplateColumns).gar("minmax(0, 1fr)").$}>
        {tiles.map((tile, index) => (
          <MosaicTile key={tile.id} tile={tile} gridColumn={getTileGridColumn(index)} />
        ))}
      </div>
    </div>
  );
}

function MosaicTile({ tile, gridColumn }: { tile: ImageMosaicTile; gridColumn: string | undefined }) {
  const [imageLoading, setImageLoading] = useState(true);
  const { imageUrl, name, onClick, isReversed, readonly } = tile;
  const tid = useTestIds({}, "mosaicTile");

  const maybeOnClick = readonly ? undefined : onClick;

  return (
    <div
      onClick={maybeOnClick}
      css={{
        // Relative: positioning context for the absolute img.
        // mw0/mh0: grid can shrink the cell.
        ...Css.relative.w100.h100.mw0.mh0.oh.$,
        ...(gridColumn ? Css.gc(gridColumn).$ : Css.$),
        ...Css.if(!!maybeOnClick).cursorPointer.$,
      }}
      {...tid}
    >
      <img
        onLoad={() => setImageLoading(false)}
        css={{
          // Absolute positioning w100/h100 + object-cover: img fills the tile and is centered (cropped).
          ...Css.absolute.top0.left0.right0.bottom0.w100.h100.objectCover.$,
          ...Css.if(!!isReversed).add("transform", "scaleX(-1)").$,
        }}
        src={imageUrl}
        alt={name}
      />
      {/* Empty full-tile layer on top of the img (sibling, not a wrapper): solid bg until load, then fades out via motion. */}
      <motion.div
        initial={{ opacity: 1 }}
        css={Css.absolute.top0.left0.right0.bottom0.bgLightBlue.z1.$}
        animate={
          !imageLoading && {
            opacity: 0,
            transition: { duration: 0.4 },
            transitionEnd: { display: "none", zIndex: -1 },
          }
        }
      />
      {name && (
        // Absolute positioning above bottom left corner of image
        <div css={Css.absolute.z2.bottom2.left2.$}>
          <span css={Css.df.aic.gap1.t12.fw400.gray900.bgWhite.o(0.8).px2.py1.$}>
            <span>{name}</span>
            <Icon name="arrow-next" size={14} />
          </span>
        </div>
      )}
    </div>
  );
}
